import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { PropertyUnitsService } from '../../services/property-units.service';
import { ScriptService } from 'src/app/core/services/script/script.service';
import { PropertyUnitLogKey } from '../../models/property-unit-log-key.enum';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AlertBoxComponent } from 'src/app/shared/components/alert-box/alert-box.component';

@Component({
    selector: 'app-property-unit-details-page',
    templateUrl: './property-unit-details.component.html',
    styleUrls: ['./property-unit-details.component.scss'],
    standalone: false
})
export class PropertyUnitDetailsComponent implements OnInit {
  unitId: string = '';
  unitData: any = null;
  spinner: boolean = true;
  errorMessage: string = '';
  relatedOrdersExpanded: boolean = true;
  changeHistoryExpanded: boolean = true;
  propertyFilesExpanded: boolean = true;

  // Property files data
  propertyFiles: any[] = [];

  // Property files labels mapping
  propertyFilesLabels: { [key: string]: string } = {};

  // Alert dialog reference
  saveDialogRef: MatDialogRef<AlertBoxComponent> | undefined;

  // Map properties
  apiLoaded: boolean = false;
  mapOptions: google.maps.MapOptions = {
    center: { lat: 23.8859, lng: 45.0792 },
    zoom: 15,
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    gestureHandling: 'none',
    draggable: false,
    scrollwheel: false,
    disableDoubleClickZoom: true,
  };
  markerOptions: google.maps.MarkerOptions = {
    draggable: false,
  };


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private titleService: Title,
    private propertyUnitsService: PropertyUnitsService,
    private scriptService: ScriptService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.unitId = this.route.snapshot.params['id'];

    // Check if Google Maps API is loaded
    this.scriptService.obsCurrentApiStatus.subscribe((status: Boolean) => {
      this.apiLoaded = status.valueOf();
    });
    
    if (this.unitId) {
      this.titleService.setTitle(`ملف الوحدة #${this.unitId} | عاين`);
      this.getUnitDetails();
    }
  }

  getUnitDetails(): void {
    this.spinner = true;
    this.propertyUnitsService.getPropertyUnitDetails(this.unitId).subscribe({
      next: (response) => {
        this.unitData = response;
        
        // Process logs to grouped format with proper messages
        if (response.logs && Array.isArray(response.logs)) {
          this.unitData.logs = this.processLogs(response.logs);
        }
        
        // Process property files from response
        if (response.files && Array.isArray(response.files)) {
          // Map files from response with proper structure
          this.propertyFiles = response.files.map((file: any) => ({
            id: file.id,
            label: file.file_type?.name || 'ملف',
            url: file.file,
            key: file.id || file.file_type?.id,
            file_type: file.file_type
          }));
        } else {
          // Fallback: create file items from individual response fields
          this.propertyFiles = Object.keys(this.propertyFilesLabels).map(key => ({
            label: this.propertyFilesLabels[key],
            url: response[key] || null,
            key: key
          }));
        }
        
        // Update map center and marker position if location available
        if (response.location) {
          const locationParts = response.location.split(',');
          if (locationParts.length === 2) {
            const lat = parseFloat(locationParts[0].trim());
            const lng = parseFloat(locationParts[1].trim());
            this.mapOptions = {
              ...this.mapOptions,
              center: { lat, lng }
            };
            this.markerOptions = {
              ...this.markerOptions,
              position: { lat, lng }
            };
          }
        }
        this.spinner = false;
      },
      error: (error) => {
        console.error('Error fetching unit details:', error);
        this.errorMessage = 'حدث خطأ أثناء تحميل بيانات الوحدة';
        this.spinner = false;
      }
    });
  }

  goToRelatedOrders(): void {
    this.router.navigate(['/pages/orders/orders-list'], {
      queryParams: { 
        property_unit: this.unitId,
        page: 1,
        page_size: 20
      }
    });
  }

  goToCustomerProfile(customerId: string): void {
    if (customerId) {
      this.router.navigate(['/pages/customers/customer-profile', customerId]);
    }
  }

  downloadFile(file: any): void {
    let fileUrl = file.url || file.file;
    
    if (fileUrl) {
      // If the URL is relative (starts with /), prepend the base URL
      if (fileUrl.startsWith('/') && !fileUrl.startsWith('//')) {
        // Get the base URL from window.location or use a relative path
        fileUrl = window.location.origin + fileUrl;
      }
      window.open(fileUrl, '_blank');
    }
  }

  getFullAddress(): string {
    if (!this.unitData) return '-';
    
    const addressParts: string[] = [];
    
    // Add building number if available
    if (this.unitData.building_number) {
      addressParts.push(this.unitData.building_number);
    }
    
    // Add street if available
    if (this.unitData.address_street) {
      addressParts.push(this.unitData.address_street);
    }
    
    // Add district if available
    if (this.unitData.district?.name) {
      addressParts.push(this.unitData.district.name);
    }
    
    // Add city if available
    if (this.unitData.address_city?.name) {
      addressParts.push(this.unitData.address_city.name);
    }
    
    // Add area if available
    if (this.unitData.address_area?.name) {
      addressParts.push(this.unitData.address_area.name);
    }
    
    return addressParts.length > 0 ? addressParts.join(' ، ') : '-';
  }

  copyClientMobile(): void {
    const mobile = this.unitData?.stakeholder?.phone_number || this.unitData?.customer_mobile;
    if (mobile) {
      const mobileWithoutSpaces = mobile.replace(/\s/g, '');
      this.copyLink(mobileWithoutSpaces);
    }
  }

  copyLink(link: string): void {
    let selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = link;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.showAlertBox();
  }

  showAlertBox(): void {
    this.saveDialogRef = this.dialog.open(AlertBoxComponent, {
      data: {
        imgSuccess: true,
        message: 'تم النسخ بنجاح',
        button: 'تم',
      },
    });
  }

  private getLogDisplayMessage(log: any): string {
    const key = log.key as PropertyUnitLogKey;
    
    switch (key) {
      case PropertyUnitLogKey.PROPERTY_ADDED:
        return 'تم إنشاء الوحدة';
      
      case PropertyUnitLogKey.FILE_ADDED:
        return 'تم إضافة ملف للوحدة';
      
      case PropertyUnitLogKey.FILE_DELETED:
        return 'تم حذف ملف من الوحدة';
      
      case PropertyUnitLogKey.PROPERTY_UPDATED_BY_ADMIN:
        return 'تم تعديل الوحدة من قبل الأدمن';
      
      case PropertyUnitLogKey.PROPERTY_UPDATED_BY_CLIENT:
        return 'تم تعديل الوحدة من قبل العميل';
      
      case PropertyUnitLogKey.ORDER_STATUS_UPDATED:
        const newValue = log.changes?.status?.new_value;
        return newValue ? `حالة الطلب: ${newValue}` : 'تم تحديث حالة الطلب';
      
      default:
        return log.change_message || log.action_display || 'تحديث';
    }
  }

  private formatLogTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return 'الآن';
    } else if (diffMins < 60) {
      return `منذ ${diffMins} ${diffMins === 1 ? 'دقيقة' : 'دقائق'}`;
    } else if (diffHours < 24) {
      return `منذ ${diffHours} ${diffHours === 1 ? 'ساعة' : 'ساعات'}`;
    } else {
      // Format as date
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      };
      return date.toLocaleDateString('ar-SA', options);
    }
  }

  private getPeriodLabel(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays === 0) {
      return 'اليوم';
    } else if (diffDays < 7) {
      return 'هذا الأسبوع';
    } else {
      return 'أقدم';
    }
  }

  private processLogs(logs: any[]): any[] {
    if (!logs || !Array.isArray(logs) || logs.length === 0) {
      return [];
    }

    // Group logs by period
    const groups: { [key: string]: any[] } = {
      'اليوم': [],
      'هذا الأسبوع': [],
      'أقدم': []
    };

    logs.forEach(log => {
      const period = this.getPeriodLabel(log.changed_at);
      const processedLog = {
        title: this.getLogDisplayMessage(log),
        time: this.formatLogTime(log.changed_at),
        ...log
      };
      groups[period].push(processedLog);
    });

    // Convert to array format
    const result = [];
    if (groups['اليوم'].length > 0) {
      result.push({ period: 'اليوم', changes: groups['اليوم'] });
    }
    if (groups['هذا الأسبوع'].length > 0) {
      result.push({ period: 'هذا الأسبوع', changes: groups['هذا الأسبوع'] });
    }
    if (groups['أقدم'].length > 0) {
      result.push({ period: 'أقدم', changes: groups['أقدم'] });
    }

    return result;
  }

  goBack(): void {
    this.router.navigate(['/pages/property-units']);
  }

  editUnit(): void {
    this.router.navigate(['/pages/property-units', this.unitId, 'edit']);
  }
}
